<template>
    <div class="flex-1 bg-white rounded-medium p-7 pb-0 md:pb-7 text-center">
        <div class="max-w-xl mx-auto flex justify-center items-center flex-col">
            <IconCongrats class="mb-6 w-[80px] h-[80px]" />
            <h1 class="title" v-if="$store.getters.isUpgrading">Upgrade successful!</h1>
            <h1 class="title" v-else>Welcome to Keap, {{ $store.getters.userInfo.firstName }}!</h1>
            <p class="text-base font-medium mb-6" v-if="$store.getters.isUpgrading">You're one step closer to achieving your goals.</p>
            <p class="text-base font-medium mb-6" v-else>Thank you for your payment. We emailed your order confirmation to: {{ $store.getters.userInfo.email }}</p>

            <div class="bg-red-light p-6 rounded-large mb-6" v-if="$store.getters.isMaintenance">
                <h2 class="font-bold text-center text-2xl mb-8">System Maintenance</h2>
                <p class="text-sm">We're currently performing system maintenance. Please contact your referring Keap partner for automations that weren't pre-installed in your app, or reach out to Keap Support at <a class="underline" href="https://keap.com/contact">https://keap.com/contact</a>.</p>
            </div>

            <div class="bg-green-10 p-6 rounded-large mb-6" v-if="$store.getters.isUpgrading">
                <p class="text-sm">Please be aware that the changes to your subscription may take a few minutes to be applied.</p>
            </div>

            <Link arrow right :href="$store.getters.appLink" class="mb-6" v-if="$store.getters.isUpgrading" small-expanded data-qa="book-now-button">Take me to my dashboard</Link>

            <!-- <div class="mb-6" v-if="$store.getters.isEventLink">
                <p class="text-base mb-2">Here's what to do next</p>
                <Link arrow right target="_blank" href="https://calendly.com/d/3ms-jkg-cf8/growth-freedom-strategy-session" class="mb-6" small-expanded data-qa="book-now-button">Schedule your first implementation appointment here</Link>
            </div> -->

            <p class="text-sm font-normal mb-2">Payment method: {{ _toTitleCase($store.getters.purchaseInfo.cardUsed.type) }} card ending in  {{ $store.getters.purchaseInfo.cardUsed.last4 }}</p>
            <p class="text-sm font-medium mb-6" v-if="!$store.getters.cartInfo.installments.enabled">Charged <span v-text="$store.getters.currencyPrefix"></span>{{ priceFormat($store.getters.purchaseInfo.totalPaid) }} today</p>
            <p class="text-sm font-medium" v-if="!!$store.getters.cartInfo.installments.enabled">Invoice total: <span v-text="$store.getters.currencyPrefix"></span>{{ priceFormat($store.getters.purchaseInfo.invoiceTotal) }} {{ !!hasInstallmentCharge ? '*' : '' }}</p>
            <p class="text-sm font-medium mb-6" v-if="!!$store.getters.cartInfo.installments.enabled">First installment: <span v-text="$store.getters.currencyPrefix"></span>{{ priceFormat($store.getters.purchaseInfo.invoiceTotal / $store.getters.cartInfo.installments.count) }}</p>
            <p class="text-sm font-normal mb-3">Here are your subscription details:</p>

            <div class="border-gray-4 border-2 rounded-medium p-7 w-full flex flex-col items-center mb-6">
                <p class="text-sm font-normal mb-3">Your {{ $store.getters.isYearly ? 'yearly' : 'monthly'}} plan: <span class="font-medium">{{ planName  }}</span></p>
                <p class="text-sm font-normal mb-6" v-if="!!$store.getters.cartInfo.installments.enabled">Installments: <span class="font-medium">{{ $store.getters.currencyPrefix }}{{ priceFormat($store.getters.purchaseInfo.invoiceTotal / $store.getters.cartInfo.installments.count) }} x {{ $store.getters.cartInfo.installments.count }}</span></p>
                <ul class="flex gap-4 flex-col md:flex-row md:items-center justify-center" :class="{
                    'mb-6': oneTimeItems.length > 0 || recurringItems.length > 0
                }">
                    <li class="flex gap-3 items-center"><Bullet class="w-[30px] h-[30px]"/> {{ $store.getters.totalContacts.toLocaleString() }} contacts</li>
                    <li class="flex gap-3 items-center"><Bullet class="w-[30px] h-[30px]"/> {{ $store.getters.totalUsers.toLocaleString() }} users</li>
                    <li class="flex gap-3 items-center" v-if="$store.getters.totalAutomations > 0"><Bullet class="w-[30px] h-[30px]"/> {{ $store.getters.totalAutomations.toLocaleString() }} automations</li>
                </ul>
                <p class="text-sm font-medium mb-2" v-if="recurringItems.length > 0">Recurring services:</p>
                <ul class="mb-6 last-of-type:mb-0" v-if="recurringItems.length > 0">
                    <li class="text-sm font-normal" v-for="item in recurringItems" :key="item.name">{{ item.name }}<span v-if="!!item.description"> – {{ item.description }}</span></li>
                </ul>
                <p class="text-sm font-medium mb-2" v-if="oneTimeItems.length > 0">One-time services:</p>
                <ul class="last-of-type:mb-0" v-if="oneTimeItems.length > 0">
                    <li class="text-sm font-normal" v-for="item in oneTimeItems" :key="item.name">{{ item.name }}<span v-if="!!item.description"> – {{ item.description }}</span></li>
                </ul>

            </div>
            <p class="text-normal" v-if="$store.getters.isPartnerLink && ($store.getters.partnerHasAccess || $store.getters.isLegacyBundleId)">Your Keap partner will be granted access to your application. If you have any questions, please contact your partner.</p>
            <p class="text-normal last:mb-0 mb-2" v-else>If you need to change your email with us, or have questions about your subscription, call our customer service team at <a href="tel:18668000004" class="underline">+1 866-800-0004</a>.</p>
            <p class="text-xs" v-if="!!hasInstallmentCharge">* Total includes installment service fee.</p>
        </div>

        <!-- Free form area -->
        <!-- TODO: Eventually this section can be moved over to craft. -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-5 mt-12 max-w-3xl mx-auto">
            <FreeMigration v-if="!$store.getters.isPartnerLink && !$store.getters.isEventLink" />
        </div>
    </div>
</template>

<script>
import IconCongrats from '@/../svg/Icon_Congrats.svg?component';
import Bullet from '@/../svg/Bullet.svg?component';
import Link from '@/Components/Link.vue';
import KeapButton from '@/Components/KeapButton.vue';
import { priceFormat, _toTitleCase } from '@/Util/helpers.js';

import CoachingBookNow from '@/BuyNow/Components/FreeForm/CoachingBookNow.vue';
import FreeMigration from '@/BuyNow/Components/FreeForm/FreeMigration.vue';


export default {
    data() {
        return {
        }
    },
    components: {
        IconCongrats,
        Bullet,
        Link,
        KeapButton,
        CoachingBookNow,
        FreeMigration,
    },
    mounted() {
    },
    props: {
    },
    methods: {
        _toTitleCase,
        priceFormat
    },
    created() {
    },
    computed: {
        planName() {
            return this.$store.getters.selectedPlan.metadata.title || this.$store.getters.selectedPlan.name
        },
        recurringItems() {
            let lineItems = this.$store.getters.cartInfo.selectedAddons;

            lineItems = lineItems.filter(item => {
                return !item.itemId.toLowerCase().includes('contact') && !item.itemId.toLowerCase().includes('user');
            }).map(item => {
                return {
                    ...this.$store.getters.selectedPlan.addons.find(addon => addon.itemId === item.itemId),
                    selectedQuantity: item.quantity
                };
            }).sort((a, b) => {
                return a.metadata.order - b.metadata.order;
            });
            lineItems = lineItems.map(item => {
                let returnObj = {
                    name: item.metadata.title || item.name,
                }
                if (item.itemId.toLowerCase().includes('text-voice')) {
                    let tier = item.metadata.tiers[item.selectedQuantity - 1];
                    returnObj.description = `${tier.includedMessages.toLocaleString()} messages & ${tier.includedMinutes.toLocaleString()} minutes`;
                }
                if (item.itemId.toLowerCase().includes('500-additional-message')) {
                    returnObj.description = `${(item.selectedQuantity * 500).toLocaleString()} text messages`;
                }
                return returnObj;
            })
            return lineItems;
        },
        hasInstallmentCharge() {
            return this.$store.getters.cartInfo.selectedCharges.some(item => {
                return item.itemId.toLowerCase().includes('installment-charge');
            });
        },
        oneTimeItems() {
            let lineItems = this.$store.getters.cartInfo.selectedCharges.filter(item => {
                return !item.itemId.toLowerCase().includes('installment-charge');
            });

            lineItems = lineItems.map(item => {
                return {
                    ...this.$store.getters.selectedPlan.charges.find(charge => charge.itemId === item.itemId),
                    selectedQuantity: item.quantity
                };
            }).sort((a, b) => {
                return a.metadata.order - b.metadata.order;
            });
            lineItems = lineItems.map(item => {
                let returnObj = {
                    name: item.metadata.title || item.name,
                    itemId: item.itemId,
                }
                return returnObj;
            });
            return lineItems;
        },
    },
    watch: {
    }
};
</script>

<style lang="postcss" scoped>
.title {
    @apply font-walsheim text-xl font-bold mb-6;
}
</style>
